import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useAmiCommand } from "@/hooks/use-ami-command";
import { Send, Terminal } from "lucide-react";

export default function AmiConsolePage() {
  const [command, setCommand] = useState("");
  const [history, setHistory] = useState<{ command: string; output: string; success: boolean }[]>([]);
  const mutate = useAmiCommand();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSend = () => {
    const cmd = command.trim();
    if (!cmd || mutate.isPending) return;

    mutate.mutate(cmd, {
      onSuccess: (data) => {
        setHistory((prev) => [...prev, { command: data.command, output: data.output, success: data.success }]);
        setCommand("");
        inputRef.current?.focus();
      },
      onError: (e) => {
        setHistory((prev) => [...prev, { command: cmd, output: (e as Error).message, success: false }]);
      },
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">AMI Console</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Run Asterisk CLI commands via AMI. Example: pjsip set logger on
        </p>
      </div>

      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm flex items-center gap-2">
            <Terminal size={14} className="text-muted-foreground" />
            Command
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4 pt-0">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              placeholder="pjsip show endpoints"
              className="flex-1 font-mono text-sm"
              autoFocus
            />
            <Button onClick={handleSend} disabled={mutate.isPending || !command.trim()} size="sm" className="gap-1.5">
              <Send size={14} />
              Send
            </Button>
          </div>
        </CardContent>
      </Card>

      {history.length > 0 && (
        <div className="space-y-3">
          <Separator />
          <h2 className="text-sm font-semibold text-muted-foreground">History</h2>
          {history.map((entry, i) => (
            <Card key={i} className={entry.success ? "border-border" : "border-destructive/40"}>
              <CardHeader className="py-2 px-4">
                <CardTitle className="text-xs font-mono text-muted-foreground">
                  $ {entry.command}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3 pt-0">
                <pre className={`text-xs font-mono whitespace-pre-wrap ${entry.success ? "text-foreground" : "text-destructive"}`}>
                  {entry.output || "(no output)"}
                </pre>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}